import { apiInterfaceForJson, apiInterfaceForFormData } from '../common/apiManager';
import { serviceConstants } from '../common/constants';
import { getUserAuthToken } from '../common/util';
import axios from 'axios';

// CSV Upload - Custom implementation to handle FormData properly
export const uploadCSV = (formData) => {
    const token = getUserAuthToken();
    return axios
        .request({
            url: serviceConstants.API_HOST_NAME + '/migration/upload-csv',
            method: 'POST',
            headers: {
                Authorization: 'Bearer ' + token
                // Let axios set Content-Type with boundary for FormData
            },
            data: formData
        })
        .then((response) => {
            return response.data;
        })
        .catch((error) => {
            console.error('CSV upload error:', error);
            if (error.response && error.response.data) {
                throw error.response.data;
            }
            throw error;
        });
};

// Migration Execution
export const executeMigration = (mappingRuleId, fileIds, options = {}) => {
    return apiInterfaceForJson('/migration/execute', 'POST', {
        mappingRuleId,
        fileIds,
        options
    });
};

// Collection Management
export const getCollectionFields = (collection) => {
    return apiInterfaceForJson(`/migration/collection-fields/${collection}`, 'GET');
};

export const getCollectionSchema = (collection) => {
    return apiInterfaceForJson(`/migration/collection-schema/${collection}`, 'GET');
};

export const getAvailableCollections = () => {
    return apiInterfaceForJson('/migration/collections', 'GET');
};

// Migration Status
export const getMigrationStatus = (jobId) => {
    return apiInterfaceForJson(`/migration/status/${jobId}`, 'GET');
};

export const getMigrationHistory = (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `/migration/history?${queryString}` : '/migration/history';
    return apiInterfaceForJson(url, 'GET');
};

export const getMigrationJobs = (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `/migration/jobs?${queryString}` : '/migration/jobs';
    return apiInterfaceForJson(url, 'GET');
};

export const getMigrationJobById = (jobId) => {
    return apiInterfaceForJson(`/migration/jobs/${jobId}`, 'GET');
};

// Migration Actions
export const cancelMigration = (jobId) => {
    return apiInterfaceForJson(`/migration/jobs/${jobId}/cancel`, 'POST', {});
};

export const rollbackMigration = (jobId) => {
    return apiInterfaceForJson(`/migration/jobs/${jobId}/rollback`, 'POST', {});
};

export const previewMigration = (mappingRuleId, fileIds, sampleSize = 10) => {
    return apiInterfaceForJson('/migration/preview', 'POST', {
        mappingRuleId,
        fileIds,
        sampleSize
    });
};

// Template Download
export const downloadMigrationTemplate = (collection) => {
    const token = getUserAuthToken();
    return axios
        .request({
            url: serviceConstants.API_HOST_NAME + `/migration/template/${collection}`,
            method: 'GET',
            headers: {
                Authorization: 'Bearer ' + token
            },
            responseType: 'blob'
        })
        .then((response) => {
            // Create a download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${collection}_template.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            return {
                status: 200,
                message: 'Template downloaded successfully'
            };
        })
        .catch((error) => {
            console.error('Download template error:', error);
            throw error;
        });
};

// Mapping Rule Services
export const getMappingRules = (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `/migration/mapping-rules?${queryString}` : '/migration/mapping-rules';
    return apiInterfaceForJson(url, 'GET');
};

export const getMappingRuleById = (id) => {
    return apiInterfaceForJson(`/migration/mapping-rules/${id}`, 'GET');
};

export const createMappingRule = (data) => {
    return apiInterfaceForJson('/migration/mapping-rules', 'POST', data);
};

export const updateMappingRule = (id, data) => {
    return apiInterfaceForJson(`/migration/mapping-rules/${id}`, 'PUT', data);
};

export const deleteMappingRule = (id) => {
    return apiInterfaceForJson(`/migration/mapping-rules/${id}`, 'DELETE');
};

// Default Rules
export const getDefaultMappingRules = () => {
    return apiInterfaceForJson('/migration/default-rules', 'GET');
};

export const initializeDefaultRules = () => {
    return apiInterfaceForJson('/migration/default-rules/initialize', 'POST', {});
};

// Additional utility functions
export const getMigrationErrors = (jobId, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `/migration/errors/${jobId}?${queryString}` : `/migration/errors/${jobId}`;
    return apiInterfaceForJson(url, 'GET');
};

export const getSampleMappings = (collection) => {
    return apiInterfaceForJson(`/migration/sample-mappings/${collection}`, 'GET');
};

// Legacy Data Services
export const getLegacyEntityTypes = () => {
    return apiInterfaceForJson('/migration/legacy-entities', 'GET');
};

export const getLegacyEntitySample = (entityType, limit = 5) => {
    return apiInterfaceForJson(`/migration/legacy-entities/${entityType}/sample?limit=${limit}`, 'GET');
};

export const getLegacyEntityHeaders = (entityType) => {
    return apiInterfaceForJson(`/migration/legacy-entities/${entityType}/headers`, 'GET');
};

export const getLegacyEntityCount = (entityType) => {
    return apiInterfaceForJson(`/migration/legacy-entities/${entityType}/count`, 'GET');
};

export const executeLegacyMigration = (mappingRuleId, entityType, options = {}) => {
    return apiInterfaceForJson('/migration/legacy-migrate', 'POST', {
        mappingRuleId,
        entityType,
        options
    });
};

export const previewLegacyMigration = (mappingRuleId, entityType, limit = 5) => {
    return apiInterfaceForJson('/migration/legacy-preview', 'POST', {
        mappingRuleId,
        entityType,
        limit
    });
};
